import { useCallback, useEffect, useRef } from 'react';

import { useApiContext } from 'context/ApiContext';

import { ITIL_TRANSLATION_TYPE_BY_SCOPE } from '../constants';
import type { ItilTranslationScope } from '../types';
import {
  clearItilTranslationCache,
  getItilTranslationCache,
  setItilTranslationCache
} from '../translationCache';
import { useItilTranslation } from './useItilTranslation';

type UseItilFieldTranslationOptions = {
  ticketId?: number;
  /** true = preview API (creación); false = persist on translate when ticketId exists */
  creationMode?: boolean;
  /** Cargar traducción guardada al montar */
  loadOnMount?: boolean;
  initialOriginal?: string;
  canTranslate?: boolean;
  scope?: ItilTranslationScope;
  /** Texto ITIL persistido en el ticket (itilTranslatedDescription) */
  persistedItilText?: string | null;
  /** true cuando el ticket ya terminó de cargar (evita limpiar caché prematuramente) */
  ticketLoaded?: boolean;
};

export function useItilFieldTranslation(
  options: UseItilFieldTranslationOptions = {}
) {
  const {
    ticketId,
    creationMode = false,
    loadOnMount = false,
    canTranslate = true,
    scope = 'description',
    persistedItilText,
    ticketLoaded = false
  } = options;

  const { itilGenius } = useApiContext();
  const itil = useItilTranslation();
  const originalRef = useRef(options.initialOriginal ?? '');
  const translationType = ITIL_TRANSLATION_TYPE_BY_SCOPE[scope];

  useEffect(() => {
    if (!loadOnMount || !ticketId) {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      let stored = await itilGenius.GetTranslationByType(
        ticketId,
        translationType
      );

      if (
        !stored &&
        scope === 'description' &&
        translationType === 'Description'
      ) {
        stored = await itilGenius.GetTranslationByType(
          ticketId,
          'CreationComment'
        );
      }

      if (cancelled) {
        return;
      }

      if (stored?.trim()) {
        setItilTranslationCache(ticketId, scope, stored);
        itil.hydrate(stored);
        return;
      }

      if (persistedItilText?.trim()) {
        setItilTranslationCache(ticketId, scope, persistedItilText.trim());
        itil.hydrate(persistedItilText.trim());
        return;
      }

      if (ticketLoaded && persistedItilText === '') {
        clearItilTranslationCache(ticketId, scope);
        itil.reset();
        return;
      }

      if (!ticketLoaded) {
        return;
      }

      const cached = getItilTranslationCache(ticketId, scope);
      if (cached) {
        itil.hydrate(cached);
      }
    };

    load().catch((): void => undefined);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, loadOnMount, scope, persistedItilText, ticketLoaded]);

  const handleOriginalChange = useCallback(
    (value: string) => {
      originalRef.current = value;
      if (itil.status === 'ready') {
        itil.markStale();
      }
    },
    [itil]
  );

  const handleTranslate = useCallback(
    async (rawDescription: string) => {
      if (!canTranslate) {
        return null;
      }
      const usePreview = creationMode || !ticketId;
      const text = await itil.translate(rawDescription, {
        ticketId: usePreview ? undefined : ticketId,
        preview: usePreview,
        translationType
      });
      if (text && ticketId && !creationMode) {
        setItilTranslationCache(ticketId, scope, text);
      }
      return text;
    },
    [canTranslate, creationMode, itil, ticketId, scope, translationType]
  );

  return {
    ...itil,
    handleOriginalChange,
    handleTranslate
  };
}
